/**
 * useAgentConfig - Custom hook for Voice Agent Config API calls
 */

import { useState, useCallback } from 'react';
import { auth } from '@/lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

export interface AgentConfig {
  tenant_id: string;
  agent_id: string | null;
  agent_phone_number_id: string | null;
  system_prompt: string | null;
  first_message: string | null;
  dynamic_variables_template: Record<string, string> | null;
  tools: Record<string, unknown>[] | null;
  active_variant: string;
  variants: Record<string, { system_prompt?: string; first_message?: string; created_at?: string }>;
  updated_at: string | null;
  last_synced_to_elevenlabs: string | null;
  last_sync_error: string | null;
}

export interface AgentConfigUpdate {
  system_prompt?: string;
  first_message?: string;
  dynamic_variables_template?: Record<string, string>;
  tools?: Record<string, unknown>[];
}

export interface CallSummary {
  conversation_id: string;
  prospect_id: string;
  prospect_name: string | null;
  company_name: string | null;
  outcome: string | null;
  duration_secs: number | null;
  completed_at: string | null;
  summary: string | null;
}

export interface CallDetail extends CallSummary {
  transcript: string;
  analysis: { summary?: string; sentiment?: string };
  recording_url: string | null;
}

export interface PerformanceMetrics {
  total_calls: number;
  qualification_rate: number;
  booking_rate: number;
  avg_duration_secs: number;
  period_days: number;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string> || {}) },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }

  return res.json();
}

export function useAgentConfig() {
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<AgentConfig>('/api/agent-config');
      setConfig(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch config');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfig = useCallback(async (update: AgentConfigUpdate): Promise<{ synced: boolean; syncError: string | null }> => {
    const result = await apiRequest<{ success: boolean; synced_to_elevenlabs: boolean; sync_error: string | null }>(
      '/api/agent-config',
      {
        method: 'PUT',
        body: JSON.stringify(update),
      }
    );
    await fetchConfig();
    return { synced: result.synced_to_elevenlabs, syncError: result.sync_error };
  }, [fetchConfig]);

  const createVariant = useCallback(async (name: string, systemPrompt: string, firstMessage?: string) => {
    await apiRequest('/api/agent-config/variants', {
      method: 'POST',
      body: JSON.stringify({ name, system_prompt: systemPrompt, first_message: firstMessage }),
    });
    await fetchConfig();
  }, [fetchConfig]);

  const activateVariant = useCallback(async (variantName: string): Promise<{ synced: boolean; syncError: string | null }> => {
    const result = await apiRequest<{ success: boolean; synced_to_elevenlabs: boolean; sync_error: string | null }>(
      `/api/agent-config/variants/${variantName}/activate`,
      { method: 'PUT' }
    );
    await fetchConfig();
    return { synced: result.synced_to_elevenlabs, syncError: result.sync_error };
  }, [fetchConfig]);

  return { config, loading, error, fetchConfig, updateConfig, createVariant, activateVariant };
}

export function useAgentCalls() {
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCalls = useCallback(async (limit = 50) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<{ calls: CallSummary[]; count: number }>(
        `/api/agent-config/calls?limit=${limit}`
      );
      setCalls(data.calls);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTranscript = useCallback(async (conversationId: string): Promise<CallDetail> => {
    return apiRequest<CallDetail>(`/api/agent-config/calls/${conversationId}`);
  }, []);

  return { calls, loading, error, fetchCalls, fetchTranscript };
}

export function useAgentMetrics() {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async (days = 30) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<PerformanceMetrics>(`/api/agent-config/metrics?days=${days}`);
      setMetrics(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  return { metrics, loading, error, fetchMetrics };
}
